/**
 * Under an answer of Catchy's (the bubble and AI Hub's chat):
 *  - WHAT WAS LOOKED UP — the screens and the filters (Jay, System Project task #1113: "Show the source under every data answer
 *    (which tool, which filter)"), so a list that came from a lookup can be told from one that did not;
 *  - IDS NOBODY LOOKED UP — the server checked the record ids in the answer against what the tools returned in that turn
 *    (aiAssistant.js ungroundedIds) and these were not; the person is told not to act on them;
 *  - "REPORT TO ADMIN" — Max, 31/10: "nếu câu nào bị giới hạn quyền thì … có nút để user click để gửi báo cáo về cho tôi". The
 *    server took Catchy's <<GAP>> line out of the answer and says what blocked it; one press marks the answer for the 16:00
 *    report (catchyReport.js), with an optional note. Shown for a named limit, a give-up, or unchecked ids.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, Check, Search, AlertTriangle } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import './GapReport.css';

export default function GapReport({ askPk, gap, gaveUp, unverified = [], lookups = [] }) {
  const { t } = useTranslation();
  const [state, setState] = useState('');      // '' | 'note' | 'busy' | 'done' | error text
  const [note, setNote] = useState('');
  const [openLookups, setOpenLookups] = useState(false);
  const blocked = !!gap || gaveUp || (unverified || []).length > 0;
  if (!askPk || (!blocked && !(lookups || []).length)) return null;
  const send = async () => {
    setState('busy');
    try {
      const r = await fetch(`${BACKEND_URL}/api/assistant/asks/${askPk}/report`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.trim() || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setState('done');
    } catch (e) { setState(e.message || 'error'); }
  };
  const cause = gap?.cause || 'tool';
  /* what is still missing: a cut list, or a failed lookup that no later call of the same tool made good (Catchy often fixes its own
     query and asks again — that one is recovered, not a gap) */
  const incomplete = (lookups || []).filter((l, i) => l.status === 'cut'
    || (l.status === 'failed' && !(lookups || []).slice(i + 1).some((x) => x.tool === l.tool && x.status === 'ok')));
  return (
    <>
      {!!(lookups || []).length && (
        <div className="gr-lookups" data-gap-lookups={lookups.length}>
          <button type="button" className="gr-lk-head" onClick={() => setOpenLookups((v) => !v)} aria-expanded={openLookups}>
            <Search size={11} /> {t('xCatchy.lookedUp')}: {[...new Set(lookups.map((l) => l.screen || l.tool))].join(' · ')}
          </button>
          {/* a lookup that failed or came back cut is said under the answer without opening anything (Jay, #1113) */}
          {incomplete.length > 0 && (
            <div className="gr-lk-warn" data-gap-lookups-incomplete>
              <AlertTriangle size={11} /> {t('xCatchy.lookupsIncomplete', { list: incomplete
                .map((l) => `${l.screen || l.tool}: ${l.status === 'failed' ? t('xCatchy.lkFailed') : t('xCatchy.lkCut', { note: l.note })}`).join('; ') })}
            </div>
          )}
          {openLookups && (
            <ul>{lookups.map((l, i) => <li key={i}><b>{l.screen || l.tool}</b>{l.args ? ` — ${l.args}` : ''}{l.status && l.status !== 'ok' ? <em className="gr-lk-bad"> ({l.status === 'failed' ? `${t('xCatchy.lkFailed')}: ${l.note}` : l.note})</em> : null}</li>)}</ul>
          )}
        </div>
      )}
      {blocked && (
        <div className="gr-strip" data-gap={gap ? cause : ((unverified || []).length ? 'unverified' : 'gaveup')} data-gap-ask={askPk}>
          {(unverified || []).length > 0 && (
            <div className="gr-why gr-unv" data-gap-unverified>
              <AlertTriangle size={12} /> <b>{t('xCatchy.unverified')}</b> {unverified.join(', ')}
            </div>
          )}
          {gap && (
            <div className="gr-why">
              <b>{t(`xCatchy.gap_${cause}`)}</b>
              {gap.need ? <span> — {gap.need}</span> : null}
            </div>
          )}
          {state === 'done' ? (
            <span className="gr-done" data-gap-done><Check size={12} /> {t('xCatchy.gapSent')}</span>
          ) : state === 'note' || state === 'busy' ? (
            <div className="gr-note">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('xCatchy.gapNotePh')} maxLength={500} data-gap-note />
              <button type="button" className="gr-btn" onClick={send} disabled={state === 'busy'} data-gap-send>{t('xCatchy.gapSend')}</button>
            </div>
          ) : (
            <>
              <button type="button" className="gr-btn" onClick={() => setState('note')} data-gap-report><Flag size={12} /> {t('xCatchy.gapReport')}</button>
              {!!state && <span className="gr-err">{state}</span>}
            </>
          )}
        </div>
      )}
    </>
  );
}
